import { Directive, ElementRef, HostListener, forwardRef } from '@angular/core';
import { NG_VALUE_ACCESSOR, ControlValueAccessor } from '@angular/forms';

/**
 * Directive to allow only integers with length validation
 * Replicates AngularJS 'only-integers-with-length' directive behavior
 * 
 * Features:
 * - Accepts only digits 0-9 and minus sign (-)
 * - Max length: 15 digits (positive) or 16 digits (negative, including minus)
 * - Formats numbers with thousand separators during input
 * - Returns clean number (without separators) to model
 */
@Directive({
    selector: '[onlyIntegersWithLength]',
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => OnlyIntegersWithLengthDirective),
            multi: true
        }
    ],
    standalone: false
})
export class OnlyIntegersWithLengthDirective implements ControlValueAccessor {
  private onChange: (value: any) => void = () => {};
  private onTouched: () => void = () => {};

  constructor(private el: ElementRef) {}

  @HostListener('input', ['$event'])
  onInput(event: any): void {
    const input = event.target as HTMLInputElement;
    let value = input.value;

    if (value) {
      // Remove all characters except digits and minus sign
      let digits = value.replace(/[^0-9-]/g, '');

      // Handle negative numbers
      if (digits.indexOf('-') === 0) {
        // Keep only first minus sign, remove others
        const hasMultipleMinus = (digits.match(/-/g) || []).length > 1;
        if (hasMultipleMinus) {
          digits = '-' + digits.replace(/-/g, '');
        }

        // Limit to 16 characters (including minus)
        if (digits.length > 16) {
          digits = digits.substring(0, 16);
        }

        // Format with thousand separators if length > 1
        if (digits.length > 1) {
          const numberPart = digits.substring(1); // Remove minus for formatting
          const formatted = this.formatNumber(numberPart);
          input.value = '-' + formatted;
        } else {
          input.value = digits;
        }
      } else {
        // Remove any minus signs from positive numbers
        digits = digits.replace(/-/g, '');

        // Limit to 15 characters
        if (digits.length > 15) {
          digits = digits.substring(0, 15);
        }

        // Format with thousand separators
        const formatted = this.formatNumber(digits);
        input.value = formatted;
      }

      // Return clean digits (without separators) to model
      const cleanValue = input.value.replace(/[^0-9-]/g, '');
      
      // Convert to number or null
      const numericValue = cleanValue === '' || cleanValue === '-' ? null : Number(cleanValue);
      
      this.onChange(numericValue);
    } else {
      this.onChange(null);
    }
  }

  @HostListener('blur')
  onBlur(): void {
    this.onTouched();
  }

  /**
   * Format number with thousand separators
   * Example: 1234567 -> 1,234,567
   */
  private formatNumber(value: string): string {
    if (!value || value === '') return '';
    
    // Remove any existing commas
    const cleanValue = value.replace(/,/g, '');
    
    // Add thousand separators
    return cleanValue.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  // ControlValueAccessor implementation
  writeValue(value: any): void {
    const input = this.el.nativeElement as HTMLInputElement;
    if (value !== null && value !== undefined) {
      const strValue = value.toString();
      if (strValue.indexOf('-') === 0) {
        const numberPart = strValue.substring(1);
        input.value = '-' + this.formatNumber(numberPart);
      } else {
        input.value = this.formatNumber(strValue);
      }
    } else {
      input.value = '';
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    const input = this.el.nativeElement as HTMLInputElement;
    input.disabled = isDisabled;
  }
}

